import { DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { ContextService } from '@services/context';
import { LogsService } from '@services/logs';
import { AddressComponent, Position, PositionAddress } from '@shared/components/map/model';
import { from, Observable, take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  readonly #isLoadedStore = signal(false);
  readonly isLoaded = this.#isLoadedStore.asReadonly();
  readonly #window = inject(DOCUMENT).defaultView as Window;
  readonly #contextService = inject(ContextService);
  readonly #logsService = inject(LogsService);

  load(): void {
    if (this.#contextService.isServer()) return;
    setOptions({
      key: environment.integrations?.googleMapsApi?.apiKey ?? '',
      v: 'weekly',
    });
    from(importLibrary('places'))
      .pipe(
        take(1),
        tap(() => {
          this.#isLoadedStore.set(true);
        })
      )
      .subscribe({
        error: (err: unknown) =>
          this.#logsService.error('MapService', 'Google Maps places library failed to load', err),
      });
  }

  getPosition$(): Observable<Position> {
    return new Observable<Position>((observer) => {
      this.#window.navigator.geolocation.getCurrentPosition(
        (position) => {
          const geocoder = new google.maps.Geocoder();
          const latlng = { lat: position.coords.latitude, lng: position.coords.longitude };
          from(
            geocoder.geocode({ location: latlng }, (results, status) => {
              if (status === 'OK') {
                if (results && results[0]) {
                  const address = this.getAddress(results[0].address_components);
                  observer.next({ status: 'OK', address: address, lat: latlng.lat, lng: latlng.lng });
                  observer.complete();
                } else {
                  observer.next({ status: 'KO', error: 'Aucun résultat trouvé', lat: latlng.lat, lng: latlng.lng });
                  observer.complete();
                }
              } else {
                observer.next({
                  status: 'KO',
                  error: 'Échec de la géocodification en raison de : ' + status,
                  lat: latlng.lat,
                  lng: latlng.lng,
                });
                observer.complete();
              }
            })
          ).subscribe({
            // Non-OK statuses are already surfaced as a KO Position by the callback above;
            // swallow the duplicate promise rejection to avoid spurious ErrorHandler noise
            error: () => undefined,
          });
        },
        (error) => observer.error(error)
      );
    });
  }

  getAddress(address_components: AddressComponent[]): PositionAddress {
    const filteredData = address_components.filter(
      (item) => item.types.includes('locality') || item.types.includes('country') || item.types.includes('postal_code')
    );
    const longNames = filteredData.map((item) => item.long_name);
    return {
      locality: longNames[0] ?? '',
      country: longNames[1] ?? '',
      postal_code: longNames[2] ?? '',
    };
  }
}
